import { Device, Group, GroupFilterCondition } from "@fairfleet/geotab";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { deviceHasAnyGroup, isDeviceInAnyGroup } from "../utils/groupHierarchy";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MAX_DEVICE_PAGES, MAX_DEVICES_LOADED, RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { DeviceSearchFilter } from "@/hooks/devices/useGroupDeviceIds";
import createGroupFilter from "@/utilities/createGroupFilter";
import { DeviceCapError, deviceCapIsFinal } from "@/utilities/error/DeviceCapError";
import { GroupIndex } from "@/utilities/groupIndex";
import { fetchAllSortedById } from "@/utilities/query/fetchAllSortedById";

export const AUDIT_DEVICE_FIELDS = [
  "id",
  "name",
  "serialNumber",
  "licensePlate",
  "vehicleIdentificationNumber",
  "groups",
  "customProperties",
  "activeTo",
];

export const AUDIT_DEVICES_QUERY_KEY = "auditDevices";

/**
 * Combines Geotab's global group filter with the page's own group selection as an
 * intersection, so the page also works standalone (spec 12.6).
 */
export function composeDeviceSearch(
  globalGroups: Group[] | undefined,
  globalCondition: GroupFilterCondition | undefined,
  pageGroups: Group[],
): DeviceSearchFilter {
  const base: DeviceSearchFilter = createGroupFilter(globalGroups, globalCondition, {});
  if (pageGroups.length === 0) return base;
  const pageCondition =
    pageGroups.length === 1
      ? { groupId: pageGroups[0].id }
      : { groupFilterConditions: pageGroups.map(g => ({ groupId: g.id })), relation: "Or" };
  if (!base.groupFilterCondition) return { groupFilterCondition: pageCondition };
  return {
    groupFilterCondition: {
      groupFilterConditions: [base.groupFilterCondition, pageCondition],
      relation: "And",
    },
  };
}

/**
 * The vehicles the audit runs over: an explicit selection when the user picked vehicles,
 * otherwise every device inside the combined group filter. Vehicles in one of `hiddenGroupIds`
 * (the vehicle types an administrator switched off for the auditors) are dropped on both paths.
 */
export function useAuditDevices(
  pageGroups: Group[],
  selectedDevices: Device[],
  groupIndex: GroupIndex,
  includeArchived: boolean,
  hiddenGroupIds: ReadonlySet<string>,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();
  const groupSearch = composeDeviceSearch(groups, groupFilterCondition, pageGroups);
  const useSelection = selectedDevices.length > 0;
  const queryClient = useQueryClient();
  const queryKey = [AUDIT_DEVICES_QUERY_KEY, activeSessionId, groupSearch, includeArchived];

  const query = useQuery<Device[]>({
    queryKey,
    enabled: !useSelection,
    placeholderData: keepPreviousData,
    ...deviceCapIsFinal(queryClient, queryKey),
    queryFn: async ({ signal }) => {
      // Like useDevicesBySearch: `fromDate` now returns only vehicles that are still active.
      // The timestamp stays out of the query key so the list is not refetched on every render.
      const search: DeviceSearchFilter = includeArchived ? groupSearch : { ...groupSearch, fromDate: new Date() };
      // Paging by id with an offset verified live on Device, deutschebahn 17.10.2026: 5000 + 2339 rows,
      // no overlap, equal to GetCountOf with the same search.
      const devices = await fetchAllSortedById<"Device", Device, DeviceSearchFilter>(geotabClient, "Device", search, {
        pageSize: RESULTS_LIMIT_DEVICE,
        maxPages: MAX_DEVICE_PAGES,
        propertySelector: { fields: AUDIT_DEVICE_FIELDS, isIncluded: true },
        signal,
      });
      // The list only names the vehicles, trips load for a few at a time (useLoadedVehicles), so it is
      // read completely. Stopping at the page bound may hide vehicles; an incomplete audit must not look complete.
      if (devices.length >= MAX_DEVICES_LOADED) throw new DeviceCapError(MAX_DEVICES_LOADED);
      return devices.filter(device => !!device.serialNumber);
    },
  });

  const devices = useMemo(() => {
    const visible = (device: Device) => !deviceHasAnyGroup(device, hiddenGroupIds, groupIndex);
    if (useSelection) {
      const pageGroupIds = pageGroups.map(g => g.id);
      return selectedDevices.filter(device => isDeviceInAnyGroup(device, pageGroupIds, groupIndex) && visible(device));
    }
    return (query.data ?? []).filter(visible);
  }, [useSelection, selectedDevices, pageGroups, hiddenGroupIds, groupIndex, query.data]);

  return {
    devices,
    isLoading: !useSelection && query.isLoading,
    isFetching: !useSelection && query.isFetching,
    isError: !useSelection && query.isError,
    error: useSelection ? null : query.error,
  };
}
